// noinspection SpellCheckingInspection

import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { Currency } from "_shared/money/currencies/currency";
import { WorkAgreementOrderField } from "work/work-agreements/_api/queries/_fields/work-agreement-order-field";
import { WorkContext } from "work/work-agreements/_api/queries/_fields/work-context";
import { WorkTeamSide } from "work/work-agreements/teams/leader-handover/_schemas/team-leader-handover-schema";
import WorkRecordCriteria from "work/work-records/work-record-criteria";
import { WorkTeamRole } from "work/work-records/work-team-role";
import WorkAgreementQuerySchema, {
  WorkAgreementFilterParametersJSON,
  WorkAgreementFilterParametersParams,
  WorkAgreementFilterParametersSchema,
  WorkAgreementOrderParametersParams,
  WorkAgreementOrderParametersSchema,
  WorkAgreementQueryJSON,
  WorkAgreementQueryParams,
} from "work/work-agreements/_api/queries/_schemas/work-agreement-query-schema";

export default class WorkAgreementQuery {
  public static create(params: WorkAgreementQueryParams): WorkAgreementQuery {
    const parsedParams = WorkAgreementQuerySchema.validate(params);

    return new WorkAgreementQuery({
      filterParameters: parsedParams.filterParameters ?? WorkAgreementFilterParameters.create({}),
      orderParameters:
        parsedParams.orderParameters ??
        WorkAgreementOrderParameters.create({
          orderBy: WorkAgreementOrderField.Start,
          orderDirection: OrderDirection.Descending,
        }),
      paginationParameters: parsedParams.paginationParameters,
    });
  }

  public static fromJSON(json: unknown): WorkAgreementQuery | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const parsedParams = WorkAgreementQuerySchema.deserialize(json);
    if (!parsedParams) return parsedParams;
    return new WorkAgreementQuery(parsedParams);
  }

  public filterParameters: WorkAgreementFilterParameters;
  public orderParameters: WorkAgreementOrderParameters;
  public paginationParameters: PaginationParameters;

  public get searchParams(): URLSearchParams {
    return new URLSearchParams([
      ...this.filterParameters.searchParams,
      ...this.orderParameters.searchParams,
      ...this.paginationParameters.searchParams,
    ]);
  }

  public get cacheKey(): string {
    const params = this.searchParams;
    const sortedEntries = [...params.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    return sortedEntries.map(([k, v]) => `${k}=${v}`).join("&");
  }

  protected constructor(params: {
    filterParameters?: WorkAgreementFilterParameters;
    orderParameters?: WorkAgreementOrderParameters;
    paginationParameters?: PaginationParameters;
  }) {
    this.filterParameters = params.filterParameters ?? WorkAgreementFilterParameters.create({});
    this.orderParameters =
      params.orderParameters ??
      WorkAgreementOrderParameters.create({
        orderBy: WorkAgreementOrderField.Start,
        orderDirection: OrderDirection.Descending,
      });
    this.paginationParameters = params.paginationParameters ?? PaginationParameters.DEFAULT;
  }

  public toJSON(): WorkAgreementQueryJSON {
    return WorkAgreementQuerySchema.serialize(this);
  }

  public isEqualTo(other: WorkAgreementQuery): boolean {
    if (!other) return false;
    return (
      this.filterParameters.isEqualTo(other.filterParameters) &&
      this.orderParameters.isEqualTo(other.orderParameters) &&
      this.paginationParameters.isEqualTo(other.paginationParameters)
    );
  }
}

export class WorkAgreementFilterParameters {
  public static create(params: WorkAgreementFilterParametersParams): WorkAgreementFilterParameters {
    const parsed = WorkAgreementFilterParametersSchema.WorkAgreementFilterParametersZ.parse(params);
    return new WorkAgreementFilterParameters(parsed);
  }

  public static fromJSON(
    json: WorkAgreementFilterParametersJSON | null | undefined
  ): WorkAgreementFilterParameters | null | undefined {
    if (isNullOrUndefined(json)) return json;
    // JSON carries ids and instants as strings, so it is decoded rather than parsed as the domain
    // shape, which wants a Guid and an AHDate and refused every id the store handed back.
    const parsed = WorkAgreementFilterParametersSchema.deserialize(json);
    return parsed ? new WorkAgreementFilterParameters(parsed) : parsed;
  }

  /**
   * The criteria a work agreement list's URL carries.
   *
   * Every criterion of `Work.WorkAgreements.WorkAgreementFilterParameters` the page can show is read
   * back (#6221), so a link the assistant writes from the criteria it counted with opens a list of
   * the same agreements. The names and spellings are `WorkRecordCriteria.WORK_AGREEMENTS`, held to
   * the server's by `work-record-list-links.json`. The page's tab decides `isArchived`, through
   * `overrides`.
   */
  public static fromSearchParams(args: {
    searchParams: URLSearchParams;
    overrides?: Partial<WorkAgreementFilterParametersParams>;
  }): WorkAgreementFilterParameters {
    const { searchParams, overrides } = args;

    const criteria = WorkRecordCriteria.WORK_AGREEMENTS.read(searchParams);
    const params = WorkAgreementFilterParametersSchema.WorkAgreementFilterParametersZ.parse({
      ...criteria,
      // A list narrowed to one work, as a version badge opens it, is there to show that work's
      // earlier versions (#7090), so it lists every version unless the URL says otherwise.
      ...(criteria.versionsOf !== undefined && criteria.currentVersionOnly === undefined
        ? { currentVersionOnly: false }
        : {}),
      isArchived: getBooleanSearchParam(searchParams, "isArchived"),
    });
    return new WorkAgreementFilterParameters({
      ...params,
      ...overrides,
    });
  }

  public readonly query?: string;
  public readonly RFPId?: Guid;
  public readonly name?: string;
  public readonly description?: string;

  public readonly clientUserId?: Guid;
  public readonly clientEntityId?: Guid;
  public readonly clientTeamMemberUserId?: Guid;
  public readonly clientTeamMemberEntityId?: Guid;

  public readonly minStart?: AHDate;
  public readonly maxStart?: AHDate;
  public readonly hasEnd?: boolean;
  public readonly minEnd?: AHDate;
  public readonly maxEnd?: AHDate;
  public readonly inProgressOn?: AHDate;

  public readonly vendorTeamRestricted?: boolean;
  /** An additional vendor, not the vendor team's leader; see `teamMemberUserId` for the leader. */
  public readonly vendorUserId?: Guid;
  public readonly vendorEntityId?: Guid;
  public readonly vendorTeamMemberUserId?: Guid;
  public readonly vendorTeamMemberEntityId?: Guid;
  public readonly vendorTeamMarketplaceId?: Guid;
  public readonly additionalVendorUserId?: Guid;
  public readonly additionalVendorEntityId?: Guid;

  /** A person with a seat on a team; `teamSide` and `teamRole` say which seat. */
  public readonly teamMemberUserId?: Guid;
  public readonly teamSide?: WorkTeamSide;
  public readonly teamRole?: WorkTeamRole;
  public readonly counterpartyEntityId?: Guid;

  public readonly feeScheduleCategoryId?: Guid;
  public readonly feeScheduleCategoryName?: string;
  public readonly feeScheduleCategoryDescription?: string;
  public readonly feeIsFixed?: boolean;
  public readonly feeIsHourly?: boolean;
  public readonly minFeeRateAmount?: number;
  public readonly maxFeeRateAmount?: number;
  public readonly feeRateCurrency?: Currency;
  public readonly feeAssignedMemberUserId?: Guid;

  public readonly teamTemplateId?: Guid;
  public readonly teamTemplateName?: string;
  public readonly feeScheduleTemplateId?: Guid;
  public readonly feeScheduleTemplateName?: string;

  public readonly minDiscount?: number;
  public readonly maxDiscount?: number;

  public readonly userId?: Guid;

  public readonly minHireDate?: AHDate;
  public readonly maxHireDate?: AHDate;
  public readonly context?: WorkContext;
  public readonly isArchived?: boolean;
  /** Unset, the server lists the latest version of each work the reader can see; false lists every version (#6810, #7090). */
  public readonly currentVersionOnly?: boolean;
  /** Every version of one work, named by its first agreement (#7090). */
  public readonly versionsOf?: Guid;
  public readonly minArchived?: AHDate;
  public readonly maxArchived?: AHDate;
  public readonly isActive?: boolean;

  public get searchParams(): URLSearchParams {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(this)) {
      if (value instanceof AHDate) {
        params.append(key, value.toISOString());
      } else if (value !== undefined) {
        params.append(key, value?.toString() ?? "null");
      }
    }
    return params;
  }

  public constructor(params: WorkAgreementFilterParametersParams) {
    Object.assign(this, params);
  }

  public toJSON(): WorkAgreementFilterParametersJSON {
    return WorkAgreementFilterParametersSchema.serialize(this);
  }

  public isEqualTo(other: WorkAgreementFilterParameters): boolean {
    const keys = new Set([...Object.keys(this), ...Object.keys(other)]);
    return [...keys].every((key) => {
      const value = this[key as keyof WorkAgreementFilterParameters];
      const otherValue = other[key as keyof WorkAgreementFilterParameters];
      if (value instanceof Guid && otherValue instanceof Guid) {
        return value.isEqualTo(otherValue);
      }
      if (value instanceof AHDate && otherValue instanceof AHDate) {
        return value.isEqualTo(otherValue);
      }
      return value === otherValue;
    });
  }
}

export class WorkAgreementOrderParameters {
  public static create(params: WorkAgreementOrderParametersParams): WorkAgreementOrderParameters {
    if (!params.orderBy) {
      params.orderBy = WorkAgreementOrderField.Start;
    }
    if (!params.orderDirection) {
      params.orderDirection = OrderDirection.Descending;
    }
    const parsed = WorkAgreementOrderParametersSchema.WorkAgreementOrderParametersZ.parse(params);
    return new WorkAgreementOrderParameters(parsed);
  }

  public static fromJSON(json: unknown): WorkAgreementOrderParameters | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const parsed = WorkAgreementOrderParametersSchema.WorkAgreementOrderParametersZ.parse(json);
    return new WorkAgreementOrderParameters(parsed);
  }

  public static fromSearchParams(
    searchParams: URLSearchParams,
    options?: {
      orderByParamName: string;
      orderDirParamName: string;
      defaultOrderBy: WorkAgreementOrderField;
      defaultOrderDir: OrderDirection;
    }
  ): WorkAgreementOrderParameters {
    options ??= {
      orderByParamName: "orderBy",
      orderDirParamName: "orderDir",
      defaultOrderBy: WorkAgreementOrderField.Start,
      defaultOrderDir: OrderDirection.Descending,
    };
    let { orderByParamName, orderDirParamName, defaultOrderBy, defaultOrderDir } = options;
    orderByParamName = orderByParamName ?? "orderBy";
    orderDirParamName = orderDirParamName ?? "orderDir";
    defaultOrderBy = defaultOrderBy ?? WorkAgreementOrderField.Start;
    defaultOrderDir = defaultOrderDir ?? OrderDirection.Descending;
    const orderByParam = searchParams.get(orderByParamName)?.toLowerCase();
    const orderDirParam = searchParams.get(orderDirParamName)?.toLowerCase();

    const orderBy =
      Object.values(WorkAgreementOrderField).find((field) => field.toLowerCase() === orderByParam) ?? defaultOrderBy;

    const orderDirection =
      Object.values(OrderDirection).find((field) => field.toLowerCase() === orderDirParam) ?? defaultOrderDir;

    return new WorkAgreementOrderParameters({ orderBy, orderDirection });
  }

  public orderDirection: OrderDirection;
  public orderBy: WorkAgreementOrderField;

  public get searchParams(): URLSearchParams {
    const params = new URLSearchParams();
    params.append("orderBy", this.orderBy);
    params.append("orderDir", this.orderDirection);
    return params;
  }

  public constructor(params: { orderBy: WorkAgreementOrderField; orderDirection: OrderDirection }) {
    this.orderBy = params.orderBy;
    this.orderDirection = params.orderDirection;
  }

  public isEqualTo(other: WorkAgreementOrderParameters): boolean {
    if (!other) return false;
    return this.orderBy === other.orderBy && this.orderDirection === other.orderDirection;
  }

  public toJSON(): WorkAgreementOrderParametersParams {
    return {
      orderBy: this.orderBy,
      orderDirection: this.orderDirection,
    };
  }
}

function getBooleanSearchParam(searchParams: URLSearchParams, paramName: string): boolean | undefined {
  const value = searchParams.get(paramName);
  if (value === null) return undefined;
  return value.toLowerCase() === "true";
}
